import backUrl from '../assets/icon/back.png';
import cheerUrl from '../assets/icon/cheer.png';
import checkUrl from '../assets/icon/check.png';
import clockUrl from '../assets/icon/clock.png';
import closeUrl from '../assets/icon/close.png';
import coinUrl from '../assets/icon/coin.png';
import eyeUrl from '../assets/icon/eye.png';
import flameUrl from '../assets/icon/flame.png';
import friendsUrl from '../assets/icon/friends.png';
import gemUrl from '../assets/icon/gem.png';
import heartUrl from '../assets/icon/heart.png';
import heartOutlineUrl from '../assets/icon/heart_outline.png';
import infoUrl from '../assets/icon/info.png';
import leaderboardUrl from '../assets/icon/leaderboard.png';
import lockUrl from '../assets/icon/lock.png';
import mapUrl from '../assets/icon/map.png';
import menuUrl from '../assets/icon/menu.png';
import notificationUrl from '../assets/icon/notification.png';
import paletteUrl from '../assets/icon/palette.png';
import profileUrl from '../assets/icon/profile.png';
import settingsUrl from '../assets/icon/settings.png';
import shareUrl from '../assets/icon/share.png';
import spinUrl from '../assets/icon/spin.png';
import starUrl from '../assets/icon/star.png';
import trophyUrl from '../assets/icon/trophy.png';

/**
 * Lookup table for the commissioned UI icon set
 * (apps/mobile/src/assets/icon/*.png — see asset brief §7).
 *
 * <p>20 icons covering HUD chrome (back / close / settings / menu /
 * profile), match-screen affordances (eye / clock / friends /
 * notification / cheer / heart / leaderboard / share), and shop /
 * gating (lock / star), plus the wallet pair (coin / gem), the
 * spin glyph, a trophy, and the wallet's streak flame (painted, in
 * place of emoji, which iOS Safari can show as boxes).
 *
 * <p>v1 icon set is full-colour 1024×1024 painterly. The {@link Icon}
 * component scales them inline via CSS — no atlas pack yet (pack lands
 * in W7 alongside the texture-atlas step in
 * {@code apps/mobile/src/assets/README.md}).
 */
const ICON_URLS: Record<IconName, string> = {
  back: backUrl,
  cheer: cheerUrl,
  check: checkUrl,
  clock: clockUrl,
  close: closeUrl,
  coin: coinUrl,
  eye: eyeUrl,
  flame: flameUrl,
  friends: friendsUrl,
  gem: gemUrl,
  heart: heartUrl,
  heart_outline: heartOutlineUrl,
  info: infoUrl,
  leaderboard: leaderboardUrl,
  lock: lockUrl,
  map: mapUrl,
  menu: menuUrl,
  notification: notificationUrl,
  palette: paletteUrl,
  profile: profileUrl,
  settings: settingsUrl,
  share: shareUrl,
  spin: spinUrl,
  star: starUrl,
  trophy: trophyUrl,
};

export type IconName =
  | 'back'
  | 'cheer'
  | 'check'
  | 'clock'
  | 'close'
  | 'coin'
  | 'eye'
  | 'flame'
  | 'friends'
  | 'gem'
  | 'heart'
  | 'heart_outline'
  | 'info'
  | 'leaderboard'
  | 'lock'
  | 'map'
  | 'menu'
  | 'notification'
  | 'palette'
  | 'profile'
  | 'settings'
  | 'share'
  | 'spin'
  | 'star'
  | 'trophy';

export interface IconProps {
  name: IconName;
  /** Pixel size — drives both width and height. Default 16. */
  size?: number;
  /** Inline alt text for screen readers. Defaults to the icon name. */
  alt?: string;
  /** Optional additional class names on the img element. */
  className?: string;
  /** Inline style overrides — useful for one-off opacity / margin
   * tweaks without writing a new class. */
  style?: React.CSSProperties;
}

/**
 * Inline image renderer for the commissioned icon set. Always uses
 * the PNG url straight (no SVG fallback yet). For sizes ≤ 24px these
 * read as crisp icons against the dark UI; smaller sizes start to
 * lose detail.
 *
 * <p>Use {@link IconName} for compile-time validation that the icon
 * exists in the set; new icons require an entry in {@link ICON_URLS}
 * AND a discriminated entry in the {@link IconName} union (TypeScript
 * will yell otherwise).
 */
export function Icon({
  name,
  size = 16,
  alt,
  className,
  style,
}: IconProps) {
  return (
    <img
      src={ICON_URLS[name]}
      width={size}
      height={size}
      alt={alt ?? name}
      className={className}
      style={{
        display: 'inline-block',
        verticalAlign: 'middle',
        ...style,
      }}
    />
  );
}
